import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductReviews = ({ productId }) => {
    const [reviews, setReviews] = useState([]);
    const [averageRating, setAverageRating] = useState(0);
    const [totalReviews, setTotalReviews] = useState(0);
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        fetchReviews();
    }, [productId]);

    const fetchReviews = async () => {
        try {
            const response = await axios.get(`${API}/products/${productId}/reviews`);
            setReviews(response.data.reviews);
            setAverageRating(response.data.average_rating);
            setTotalReviews(response.data.total_reviews);
        } catch (error) {
            console.error('Error fetching reviews:', error);
        }
    };

    const handleSubmitReview = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await axios.post(`${API}/reviews`, {
                product_id: productId,
                rating,
                comment
            });
            toast.success('Review submitted successfully!');
            setShowReviewForm(false);
            setComment('');
            setRating(5);
            fetchReviews();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit review');
        } finally {
            setLoading(false);
        }
    };

    const renderStars = (rating) => {
        return [...Array(5)].map((_, index) => (
            <Star
                key={index}
                className={`w-4 h-4 ${index < rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-600'}`}
                strokeWidth={1.5}
            />
        ));
    };

    const renderRatingSelector = () => {
        return (
            <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        className="transition-transform hover:scale-110"
                        data-testid={`rating-star-${star}`}
                    >
                        <Star
                            className={`w-8 h-8 ${star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-600'}`}
                            strokeWidth={1.5}
                        />
                    </button>
                ))}
            </div>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="mt-12" data-testid="product-reviews">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h3 className="text-2xl font-semibold mb-2">Customer Reviews</h3>
                    {totalReviews > 0 && (
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                                {renderStars(Math.round(averageRating))}
                            </div>
                            <span className="text-xl font-bold" data-testid="average-rating">{averageRating}</span>
                            <span className="text-muted-foreground">({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})</span>
                        </div>
                    )}
                </div>
                {isAuthenticated && !showReviewForm && (
                    <button 
                        onClick={() => setShowReviewForm(true)}
                        className="btn-primary text-sm px-6 py-3"
                        data-testid="write-review-button"
                    >
                        Write a Review
                    </button>
                )}
            </div>

            {showReviewForm && (
                <form onSubmit={handleSubmitReview} className="bg-card border border-border p-6 mb-8" data-testid="review-form">
                    <h4 className="text-lg font-semibold mb-4">Write Your Review</h4>
                    
                    <div className="mb-4">
                        <label className="block text-sm mb-2">Rating</label>
                        {renderRatingSelector()}
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm mb-2">Your Review</label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            className="w-full bg-black border-white/20 border focus:border-primary rounded-none h-32 px-4 py-3 text-sm placeholder:text-white/30 resize-none"
                            placeholder="Share your experience with this product..."
                            required
                            data-testid="review-comment-input"
                        />
                    </div>

                    <div className="flex gap-3">
                        <button 
                            type="submit"
                            disabled={loading}
                            className="btn-primary flex items-center gap-2"
                            data-testid="submit-review-button"
                        >
                            <Send className="w-4 h-4" />
                            {loading ? 'Submitting...' : 'Submit Review'}
                        </button>
                        <button 
                            type="button"
                            onClick={() => setShowReviewForm(false)}
                            className="btn-secondary"
                            data-testid="cancel-review-button"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            <div className="space-y-6" data-testid="reviews-list">
                {reviews.length === 0 ? (
                    <p className="text-center py-12 text-muted-foreground">No reviews yet. Be the first to review this product!</p>
                ) : (
                    reviews.map((review) => (
                        <div 
                            key={review.id}
                            className="bg-card border border-border p-6"
                            data-testid={`review-${review.id}`}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        {renderStars(review.rating)}
                                    </div>
                                    <p className="font-semibold">{review.user_name}</p>
                                </div>
                                <span className="text-sm text-muted-foreground">{formatDate(review.created_at)}</span>
                            </div>
                            <p className="text-muted-foreground">{review.comment}</p>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ProductReviews;
